import { axiosInstance } from "./apiClient";

export interface PreventivoRow {
  id: string;
  preventivo_id: string;
  position: number;
  query_text: string;
  quantity: number;
  unit: string;
  code?: string | null;
  notes?: string | null;
  source_ref?: string | null;
  candidates?: Candidate[] | null;
  selected_item_uid?: string | null;
  overridden_metadata?: Record<string, any> | null;
  confirmed: boolean;
  feedback_sent: boolean;
  /** Set when the searcher pipeline failed for this row — requires manual matching. */
  match_error?: string | null;
  // v2 structural fields
  row_type?: "item" | "section";
  level?: number;
  numero?: string | null;
  disciplina?: "ELE" | "MEC" | null;
  descrizione_breve?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export type MatchType = "codice" | "exact" | "acceptable" | "excluded" | "manual" | "campione";

export interface Candidate {
  uid: string;
  final_score: number;
  rerank_score: number;
  match_percent?: number | null;
  match_type?: MatchType | null;
  /** True when the user explicitly picked this item via "Ricerca manuale". */
  manually_selected?: boolean;
  reasoning?: string | null;
  /** Omitted in slim preventivo GET responses — use metadata.title / descrizione_breve instead. */
  text?: string;
  metadata: Record<string, any>;
}

/** Display label for a candidate; safe when `text` is absent (slim API). */
export function getCandidateLabel(candidate: Candidate): string {
  const meta = candidate.metadata ?? {};
  if (meta.title) return String(meta.title);
  if (meta.descrizione_breve) return String(meta.descrizione_breve);
  if (candidate.text) {
    const head = candidate.text.split(" — ")[0]?.trim();
    if (head) return head;
  }
  return candidate.uid;
}

export interface Preventivo {
  id: string;
  name: string;
  description?: string | null;
  status: "open" | "processing" | "ready" | "error" | "closed";
  file_original_name?: string | null;
  total_rows: number;
  processed_rows: number;
  created_by?: string | null;
  created_at: string;
  updated_at?: string | null;
  closed_at?: string | null;
  // v2 metadata
  categoria?: string | null;
  anno?: number | null;
  nome_cantiere?: string | null;
  codice_progetto?: string | null;
  source_files?: Record<string, string> | null;
  rows?: PreventivoRow[] | null;
}

export interface PreventivoUpdateData {
  name?: string;
  description?: string;
  categoria?: string;
  anno?: number;
  nome_cantiere?: string;
  codice_progetto?: string;
}

export const preventiviApi = {
  list: async (params?: {
    q?: string;
    status_filter?: string;
    categoria?: string;
    anno?: number;
  }): Promise<Preventivo[]> => {
    const res = await axiosInstance.get("/preventivi", { params });
    return res.data;
  },

  create: async (data: {
    name: string;
    description?: string;
    categoria?: string;
    anno?: number;
    nome_cantiere?: string;
    codice_progetto?: string;
    file?: File;
    file_ele?: File;
    file_mec?: File;
    items_json?: string;
  }): Promise<Preventivo> => {
    const form = new FormData();
    form.append("name", data.name);
    if (data.description) form.append("description", data.description);
    if (data.categoria) form.append("categoria", data.categoria);
    if (data.anno != null) form.append("anno", String(data.anno));
    if (data.nome_cantiere) form.append("nome_cantiere", data.nome_cantiere);
    if (data.codice_progetto) form.append("codice_progetto", data.codice_progetto);

    if (data.items_json) {
      form.append("items_json", data.items_json);
    } else if (data.file_ele || data.file_mec) {
      if (data.file_ele) form.append("file_ele", data.file_ele);
      if (data.file_mec) form.append("file_mec", data.file_mec);
    } else if (data.file) {
      form.append("file", data.file);
    }

    const res = await axiosInstance.post("/preventivi", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60_000,
    });
    return res.data;
  },

  get: async (id: string): Promise<Preventivo> => {
    const res = await axiosInstance.get(`/preventivi/${id}`, {
      params: { slim: true },
      timeout: 60_000,
    });
    return res.data;
  },

  update: async (id: string, data: PreventivoUpdateData): Promise<Preventivo> => {
    const res = await axiosInstance.patch(`/preventivi/${id}`, data);
    return res.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/preventivi/${id}`);
  },

  process: async (id: string): Promise<{ status: string }> => {
    const res = await axiosInstance.post(`/preventivi/${id}/process`);
    return res.data;
  },

  addRow: async (
    id: string,
    data: {
      query_text: string;
      quantity?: number;
      unit?: string;
      code?: string;
      notes?: string;
      source_ref?: string;
      disciplina?: "ELE" | "MEC";
      row_type?: "item" | "section";
      level?: number;
      numero?: string;
      descrizione_breve?: string;
      /** Insert immediately after this row instead of appending at the end. */
      after_row_id?: string;
    }
  ): Promise<PreventivoRow> => {
    const res = await axiosInstance.post(`/preventivi/${id}/rows`, data);
    return res.data;
  },

  updateRow: async (
    preventivoId: string,
    rowId: string,
    data: Partial<PreventivoRow>
  ): Promise<PreventivoRow> => {
    const res = await axiosInstance.patch(`/preventivi/${preventivoId}/rows/${rowId}`, data);
    return res.data;
  },

  deleteRow: async (preventivoId: string, rowId: string): Promise<void> => {
    await axiosInstance.delete(`/preventivi/${preventivoId}/rows/${rowId}`);
  },

  reorderRows: async (preventivoId: string, ordered_row_ids: string[]): Promise<void> => {
    await axiosInstance.patch(`/preventivi/${preventivoId}/rows/reorder`, { ordered_row_ids });
  },

  close: async (id: string): Promise<{ status: string }> => {
    const res = await axiosInstance.post(`/preventivi/${id}/close`);
    return res.data;
  },

  rematch: async (
    id: string,
    mode: "missing" | "all"
  ): Promise<{ queued: number; mode: string; message?: string }> => {
    const res = await axiosInstance.post(`/preventivi/${id}/rematch`, { mode });
    return res.data;
  },

  rematchRow: async (
    preventivoId: string,
    rowId: string
  ): Promise<{ status: string; row_id: string }> => {
    const res = await axiosInstance.post(`/preventivi/${preventivoId}/rows/${rowId}/rematch`);
    return res.data;
  },

  importFile: async (
    id: string,
    file: File,
    disciplina: "ELE" | "MEC"
  ): Promise<{ added: number; disciplina: string; filename: string }> => {
    const form = new FormData();
    form.append("file", file);
    form.append("disciplina", disciplina);
    const res = await axiosInstance.post(`/preventivi/${id}/import-file`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60_000,
    });
    return res.data;
  },

  exportUrl: (id: string): string => {
    return `/preventivi/${id}/export?format=xlsx`;
  },
};
